import L from "leaflet";
import type { Polygon, Position } from "geojson";
import "leaflet/dist/leaflet.css";
import mustache from "mustache";
import { GOOGLE_MAPS_API_KEY } from "../../../../../../constantes";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { MapZoomButton } from "../../../../implementations/map/MapZoomButton";
import { OrtomosaicoControl } from "../../../../implementations/map/OrtomosaicoControl";
import { LotesWmsLayer } from "./LotesWmsLayer";
import {
  LoteVertexEditEngine,
  OperacionVertice,
  OperacionVerticeServidor,
} from "./LoteVertexEditEngine";
import { LotesWfsService } from "./LotesWfsService";
import { fx_filaAvisos } from "./mapAvisos";
import { LoteSplitState } from "./LoteSplitState";
import {
  AVISO_DIVIDIR_TRAZANDO,
  AVISO_EDITAR_VERTICES,
  AVISOS_PASO4,
  LoteSplitHerramienta,
  LoteSplitToolbar,
} from "./LoteSplitToolbar";
import template from "./loteSplitMapTemplate.html";

mustache.parse(template);

let LOTE_SPLIT_MAP_ID = 0;

/* `HIGHLIGHT_COLOR`, `SELECCION_FILL_OPACITY` y `SELECCIONADO_FILL_OPACITY` se
   eliminaron: eran los estilos que este render le aplicaba a la capa Leaflet de
   un lote, y un lote ya no se dibuja en Leaflet (lo pinta sólo el WMS, ver
   `LotesWmsLayer`). Sus equivalentes viven ahora como SLD y se aplican sobre una
   capa WMS filtrada por `id`:

     - cualquier lote resaltado —elegido con un click, con el foco del input de
       nombre, esperando la confirmación de Borrar, o los dos de Unir— →
       `sextante_lote_temporal_seleccionado.sld`: azul aguamarina `#40E0D0`,
       borde de 3 px, sin relleno. **Es uno solo para los tres modos**, y es el
       mismo resalte del paso 3. Antes eran dos estilos amarillos distintos
       (borde `#FFFF00` para el seleccionado, relleno `#FFFF00` al 35% para el
       que esperaba confirmación, que era `SELECCION_FILL_OPACITY`).

   Los 15 colores de `LOTE_COLORS` no tienen reemplazo por WMS: el color salía
   de la POSICIÓN del lote en el arreglo del cliente, y esa posición no existe
   en la base (ver el comentario de `sextante_lote_temporal.sld`). Los lotes
   quedan todos del naranja del AOI, como las piezas del paso 3.

   Y el `bringToFront()` de la pieza resaltada también se fue, con el problema
   que resolvía: dentro de un pane, el orden de alta decidía quién tapaba a
   quién, y el borde del resalte quedaba cubierto por los lotes vecinos. Ahora el
   resalte es una capa aparte POR ENCIMA de la de los lotes, así que no hay nada
   que traer al frente. */

/** Ícono del card con el nombre del paso 4 (pedido explícito): la grilla de
 *  cuatro paneles de `ph-windows-logo` es lo más parecido que hay en el
 *  catálogo a un predio dividido en lotes. Distinto del `ph-polygon` del paso
 *  3 a propósito — ahí se traza UN contorno, acá se reparte en partes. */
const ICONO_PASO = "ph-windows-logo";

/** Mapa de división de lotes (paso 4 del wizard de registro de finca) —
 *  arranca encuadrado al AOI de la finca (mismo patrón de `fitBounds` que
 *  `BaseMapRender._fitToLotes`; a diferencia de `AoiMapRender`, acá no hay
 *  buscador de ubicación, ya se sabe dónde está la finca).
 *
 *  **Los lotes no se dibujan en Leaflet.** Los pinta GeoServer por WMS
 *  (`LotesWmsLayer`), con su nombre escrito por el `TextSymbolizer` del SLD en
 *  vez de por un tooltip permanente. Lo único que este render pone en el mapa
 *  son los vectores que el usuario está manipulando: la línea de corte
 *  (`LoteSplitLineEngine`) y el handle del vértice que se arrastra
 *  (`LoteVertexEditEngine`).
 *
 *  Es la misma metodología del paso 3, y por los mismos motivos: un `<path>`
 *  más un `<div>` de tooltip por lote, que Leaflet reposiciona en cada
 *  movimiento del mapa, es lo que trababa el paneo de una finca con muchos
 *  lotes; y cada cambio de selección rehacía TODAS las capas
 *  (`clearLayers()` + un `L.geoJSON` nuevo por lote) para cambiarle el color a
 *  una.
 *
 *  **Sin capas, el click se resuelve contra el modelo**
 *  (`LoteSplitState.enElPunto`, con turf): un raster no se puede clickear por
 *  feature — el pane del WMS tiene `pointer-events: none`. Igual que en el paso
 *  3.
 *
 *  El estado (`LoteSplitState`) es **compartido** con `LoteSplitListRender`
 *  — lo inyecta quien monta ambos (`RegistroFincaPaso4Render`), este render
 *  no lo crea ni lo inicializa. */
export class LoteSplitMapRender implements IConnectRender {
  private readonly _mapId: string;
  private _map: L.Map | null = null;
  private _zoomIn = new MapZoomButton("ph-plus", "Acercar", (m) => m.zoomIn());
  private _zoomOut = new MapZoomButton("ph-minus", "Alejar", (m) => m.zoomOut());
  private _toolbar = new LoteSplitToolbar();
  private _editEngine: LoteVertexEditEngine | null = null;
  private _highlightedId: number | null = null;
  private _avisoEl: HTMLElement | null = null;
  private _herramienta: LoteSplitHerramienta | null = null;
  /** Piezas elegidas en el modo Unir, en orden. Al llegar a 2 se valida y se
   *  pide confirmación; se vacía al terminar o al salir del modo. */
  private _seleccionUnir: number[] = [];
  /** Lote elegido con un click, o `null`. Es una selección PERSISTENTE y
   *  propia del usuario —distinta de `_seleccionUnir`, que dura lo que dura una
   *  operación, y de `_highlightedId`, que es el resalte pasajero que dispara
   *  el foco en la tabla de lotes—. Se conserva mientras se cambia de
   *  herramienta; solo la limpia volver a clickear el mismo lote, o que ese
   *  lote deje de existir. */
  private _seleccionado: number | null = null;

  /** @param _avisoPaso Nombre del paso del wizard, que se pinta como primer
   *   card de la fila de avisos y NO se oculta nunca (a diferencia del de la
   *   herramienta activa). El texto lo pone quien construye este render
   *   (`RegistroFincaPaso4Render`) y no este componente: el mapa no sabe en
   *   qué paso de qué wizard está montado — mismo criterio que `AoiMapRender`
   *   en el paso 3. `null` = solo el aviso de la herramienta, como antes. */
  /** @param _registroId Registro temporal en curso. Habilita la capa WMS de
   *   POLÍGONOS PADRE: la Api filtra por él, igual que en el paso 3.
   *
   *   **Por qué el paso 4 también la muestra.** Los lotes de este paso son la
   *   subdivisión de esas piezas, y hasta acá al pasar del paso 3 al 4 los
   *   contornos padre simplemente desaparecían: el usuario perdía la referencia
   *   de dónde estaba el perímetro que acababa de dibujar. Es la misma capa, el
   *   mismo filtro y el mismo estilo — no hay nada que configurar aparte.
   *
   *   Va DEBAJO de los lotes (pane propio con menor z-index) y sin capturar
   *   clicks, así no estorba al corte ni a la edición de vértices.
   *
   *   `null` = sin registro en curso, el paso 4 funciona como antes. */
  /** `conOrtomosaico` monta el toggle del ortomosaico de la finca.
   *
   *  Lo pide el wizard sólo en modo EDICIÓN: ahí el predio ya existe y puede
   *  tener un ortomosaico cargado, que es la referencia real contra la que el
   *  usuario corrige los lotes. Creando una finca de cero no hay ninguno, y el
   *  control se apaga solo igual (ver `OrtomosaicoControl`). */
  constructor(
    private readonly _state: LoteSplitState,
    private readonly _avisoPaso: string | null = null,
    private readonly _registroId: string | null = null,
    private readonly _conOrtomosaico = false,
  ) {
    LOTE_SPLIT_MAP_ID += 1;
    this._mapId = LOTE_SPLIT_MAP_ID.toString();
  }

  render(): string {
    return mustache.render(template, { mapId: this._mapId });
  }

  connect(): void {
    const container = document.getElementById(
      `loteSplitMapInstance-${this._mapId}`,
    );
    if (!container) return;

    this._map = L.map(container, {
      zoomControl: false,
      preferCanvas: true,
      // **Explícito, y tiene que coincidir con el `maxZoom` de las capas WMS.**
      //
      // Sin esto el mapa deduce su rango de zoom de las capas que estén
      // montadas, y las de este paso se montan y desmontan solas (los resaltes,
      // los vértices). Un rango que cambia según qué capa esté viva es
      // exactamente donde se rompe la contabilidad de niveles de tiles.
      //
      // El invariante es: `maxZoom` de CADA capa WMS >= `maxZoom` del mapa. Si
      // una capa se queda corta, en los niveles de más arriba `_pruneTiles` le
      // hace `_removeAllTiles()` (se ve vacía) y `_setView` le deja
      // `tileZoom = undefined`, con lo que `_update()` deja de pedir tiles. Las
      // tres capas de acá y el satelital declaran 20, igual que el paso 3.
      //
      // `maxNativeZoom` NO se setea a propósito: serviría para que una capa sin
      // datos más allá de cierto nivel reuse el tile de ese nivel escalado, y
      // acá GeoServer dibuja a cualquier zoom desde la geometría — fijarlo sólo
      // daría una capa borrosa en los niveles altos.
      maxZoom: 20,
    });

    L.tileLayer(
      `https://mt{s}.google.com/vt?lyrs=y&x={x}&y={y}&z={z}&key=${GOOGLE_MAPS_API_KEY}`,
      { maxZoom: 20, subdomains: ["0", "1", "2", "3"] },
    ).addTo(this._map);

    // **Acá se montaba un `AoiWmsLayer` en modo sólo-trazo, y se fue
    // (2026-10-23).** Existía nada más que para que GeoServer renderizara lo
    // que la herramienta Dibujar estaba trazando; ahora eso lo dibuja Leaflet
    // en el acto, sin tiles ni viaje de red, así que esta instancia no tenía
    // ya ninguna capa que pintar. Los polígonos padre de este paso tampoco
    // salían de ella: en el paso 4 el padre no se dibuja — se lee en memoria
    // para el snap del dibujo (ver `setPoligonosPadre`).

    // Los LOTES, por WMS. Va después de la capa de polígonos padre para quedar
    // encima: los lotes son la subdivisión de esas piezas.
    if (this._registroId) {
      this._wmsLotes = new LotesWmsLayer(this._map, this._registroId);
      this._wmsLotes.addTo();

      // **El trazo de la herramienta Dibujar ya no viaja al servidor**
      // (2026-10-23): lo dibuja Leaflet dentro del motor, igual que en el paso
      // 3 —es el mismo motor—, así que acá no se arma ningún
      // `VerticesWfstService` ni se repintan tiles por click. Lo que GeoServer
      // sigue pintando es lo mismo de antes: los lotes guardados y los
      // polígonos padre.
    }

    this._state.addOnChangeListener(this._renderLotes);

    const mapControlsRight = document.getElementById(
      `loteSplitMapControlsRight-${this._mapId}`,
    );
    if (mapControlsRight) {
      this._zoomIn.init(mapControlsRight, this._map);
      this._zoomOut.init(mapControlsRight, this._map);
      if (this._conOrtomosaico) {
        this._ortomosaico = new OrtomosaicoControl();
        // Con label: acá el botón comparte columna con las herramientas del paso,
        // que llevan todas su texto.
        this._ortomosaico.init(mapControlsRight, this._map, true);
      }
      this._toolbar.setOnHerramientaChange(this._onHerramientaChange);
      this._toolbar.setOnDividir((loteTempId, linea) =>
        this._onDividir?.(loteTempId, linea),
      );
      this._toolbar.setOnUnir((idA, idB) => this._onUnir?.(idA, idB));
      this._toolbar.setOnDibujado((trazo) => this._onDibujado?.(trazo));
      // Sin el registro desde el 2026-10-27: la barra lo usaba sólo para leer de
      // la base el lote a dividir (`LeerLoteTemporal`), y ahora usa la copia del
      // estado (ver `LoteSplitToolbar.comenzarCorteDeAsync`).
      this._toolbar.init(mapControlsRight, this._map, this._state);
      // Los polígonos padre que ya se hayan leído (el paso los pide al entrar,
      // ver `setPoligonosPadre`). Si todavía no llegaron, los pone esa misma
      // llamada en cuanto vuelva: la toolbar se arma igual.
      this._toolbar.setPoligonosPadre(this._poligonosPadre);
      this._editEngine = new LoteVertexEditEngine(this._map, this._alOperarVertice);
    }

    // El click sobre un lote, resuelto contra el MODELO. Reemplaza al
    // `sublayer.on("click")` que había por lote: sin capas de Leaflet no hay a
    // qué colgarlo, y el pane del WMS no recibe eventos a propósito.
    this._map.on("click", this._alClickDelMapa);

    // El hover del modo Editar ya NO pasa por acá: el motor de vértices
    // escucha el `mousemove` por su cuenta, porque lo que necesita no es "en
    // qué lote está el cursor" sino a qué TRAMO se acercó — el handle aparece
    // junto al borde, no dentro del polígono.

    this._avisoEl = document.getElementById(`loteSplitMapAviso-${this._mapId}`);
    // Primer pintado: el card con el nombre del paso tiene que estar desde que
    // se monta el mapa, sin esperar a que el usuario active una herramienta.
    this._refreshAviso();

    this._renderLotes();
    // Remedir ANTES de encuadrar: `fitBounds` calcula centro y zoom con el
    // tamaño que Leaflet tiene cacheado, así que encuadrar primero y remedir
    // después dejaba el encuadre hecho para un contenedor que ya no existe.
    this._map.invalidateSize();
    this._fitToLotes();

    // Leaflet mide el contenedor UNA vez y cachea ese tamaño. Acá eso no
    // alcanza: `RegistroFincaModalRender._mount` conecta este mapa ANTES de
    // llenar el panel de "Lotes creados", y mientras está vacío ese panel va
    // oculto (`:empty` → `display: none`, `crearFincaPage.css`). O sea que el
    // mapa se construye y se encuadra con el 100% del ancho, y un instante
    // después el sidebar de 320px aparece y le come ese espacio: el mapa se
    // veía "centrado" pero la mitad de los lotes quedaba detrás del panel.
    // No se invierte el orden de `_mount` porque la tabla del panel lee el
    // `LoteSplitState`, que recién se inicializa en `connectBody`.
    //
    // El observer remide en cada cambio del contenedor (ese primer ajuste,
    // redimensionar la ventana, cruzar el breakpoint de 1024px) y, mientras
    // el usuario no haya movido el mapa, vuelve a encuadrar los lotes. La
    // primera notificación llega después del layout de esta misma tarea, ya
    // con el panel lleno, así que el encuadre inicial queda con el ancho real.
    this._resizeObserver = new ResizeObserver(this._alRedimensionar);
    this._resizeObserver.observe(container);

    // Cualquier drag o zoom del usuario congela el encuadre automático: desde
    // ahí un resize solo remide, no le cambia la vista que él eligió.
    this._map.on("dragstart", this._marcarVistaDelUsuario);
    this._map.on("zoomstart", this._marcarVistaDelUsuario);
  }

  private _resizeObserver: ResizeObserver | null = null;
  /** `true` en cuanto el usuario arrastra o hace zoom (rueda, botones +/−).
   *  Desde ahí el observer deja de re-encuadrar. */
  private _vistaDelUsuario = false;
  /** Encendido mientras corre un `fitBounds` propio, para que su `zoomstart`
   *  no se confunda con un zoom del usuario. */
  private _encuadrando = false;

  private _alRedimensionar = (): void => {
    if (!this._map) return;
    this._map.invalidateSize();
    if (!this._vistaDelUsuario) this._fitToLotes();
  };

  private _marcarVistaDelUsuario = (): void => {
    if (!this._encuadrando) this._vistaDelUsuario = true;
  };

  dispose(): void {
    this._ortomosaico?.dispose();
    this._ortomosaico = null;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._map?.off("dragstart", this._marcarVistaDelUsuario);
    this._map?.off("zoomstart", this._marcarVistaDelUsuario);
    this._map?.off("click", this._alClickDelMapa);
    this._wmsLotes?.dispose();
    this._wmsLotes = null;
    this._zoomIn.dispose();
    this._zoomOut.dispose();
    this._editEngine?.dispose();
    this._editEngine = null;
    this._toolbar.dispose();
    this._map?.remove();
    this._map = null;
  }

  /** Notifica que hay un corte que mandar al servidor: el lote elegido (su id
   *  del registro temporal) y la línea tal como se trazó.
   *
   *  Lo atiende quien tiene el registro y el servicio gRPC
   *  (`RegistroFincaPaso4Render`): ni este render ni la toolbar hablan con el
   *  backend. */
  setOnDividir(
    handler: (loteTempId: string, linea: Position[]) => void,
  ): void {
    this._onDividir = handler;
  }

  /** Notifica que el usuario eligió un lote con un click, con NINGUNA
   *  herramienta activa (`null` al soltarlo).
   *
   *  Lo usa el paso 4 para traer el input de nombre de ese lote a la vista. Sólo
   *  se dispara sin herramienta activa a propósito: con Editar o Dibujar
   *  encendidas, mover el foco al sidebar se lo quitaría al mapa en medio de una
   *  operación. */
  setOnLoteSeleccionado(handler: (id: number | null) => void): void {
    this._onLoteSeleccionado = handler;
  }

  /** Notifica que hay una unión confirmada que mandar al servidor (ver
   *  `LoteSplitToolbar.confirmarUnion`). */
  setOnUnir(handler: (loteTempIdA: string, loteTempIdB: string) => void): void {
    this._onUnir = handler;
  }

  /** Notifica que hay un lote dibujado que mandar al servidor: llegan los PUNTOS
   *  del trazo, no un polígono — el lote lo arma el servidor, y el trazo puede
   *  venir abierto para que lo cierre contra el borde del padre (ver
   *  `LoteSplitToolbar.setOnDibujado`). */
  setOnDibujado(handler: (trazo: Position[]) => void): void {
    this._onDibujado = handler;
  }

  /** Los polígonos padre del registro, **en memoria y SIN renderizar**.
   *
   *  **Para qué.** Para que la herramienta Dibujar pueda pegarse al perímetro
   *  del predio y autocerrarse contra él. Los anillos de snap salían sólo de
   *  los lotes, con el argumento de que el borde de un padre es la unión de
   *  los bordes de sus lotes; eso vale mientras cada pieza esté enteramente
   *  cubierta por lotes, y deja de valer apenas se borra uno — justo donde el
   *  usuario va a dibujar el que lo reemplaza.
   *
   *  **Por qué llegan de afuera y no se calculan acá.** Calcularlos exigía
   *  unir todos los lotes con turf, y eso congelaba la app un par de segundos
   *  cada vez (por eso se habían quitado, 2026-10-09). Ahora los pide el paso
   *  al entrar, al servidor, que es quien los mantiene al día
   *  (`RecalcularAreaDesdeLotesAsync`), y acá sólo se guardan.
   *
   *  **No se dibujan.** En el paso 4 lo que se ve son los lotes; estas
   *  geometrías son datos para validar y para pegar, no una capa. Se puede
   *  llamar antes o después de `connect()`. */
  setPoligonosPadre(poligonos: readonly Polygon[]): void {
    this._poligonosPadre = poligonos;
    this._toolbar.setPoligonosPadre(poligonos);
  }

  private _onDibujado: ((trazo: Position[]) => void) | null = null;

  private _onUnir:
    | ((loteTempIdA: string, loteTempIdB: string) => void)
    | null = null;

  private _onLoteSeleccionado: ((id: number | null) => void) | null = null;

  private _onDividir:
    | ((loteTempId: string, linea: Position[]) => void)
    | null = null;

  /** Resalta (borde aguamarina) la pieza `id` en el mapa — foco del
   *  input de nombre en `LoteSplitListRender` (mismo patrón que el control
   *  original, `setEditingHighlightV4`). `null` quita el resalte. */
  setHighlighted(id: number | null): void {
    this._highlightedId = id;
    this._renderLotes();
  }

  /** Repinta lo que depende del estado: los resaltes y los handles del modo
   *  Editar. **Los lotes ya no se dibujan acá** — los pinta el WMS, y sus tiles
   *  no se repiden desde este método sino cuando el servidor confirma el cambio
   *  (ver `refrescarLotes`): hasta ese momento la base todavía tiene lo
   *  anterior.
   *
   *  Lo que antes hacía este método —`clearLayers()` y un `L.geoJSON` nuevo por
   *  lote, con su tooltip y sus listeners— corría en CADA cambio de selección.
   *  Ahora un cambio de selección es un GetMap acotado a la caja de un lote. */
  private _renderLotes = (): void => {
    // Un corte, una unión o un borrado pueden haberse llevado el lote elegido:
    // dejar su id vivo resaltaría al que herede ese número.
    if (
      this._seleccionado !== null &&
      !this._state.getAll().some((e) => e.id === this._seleccionado)
    ) {
      this._seleccionado = null;
    }
    if (
      this._highlightedId !== null &&
      !this._state.getAll().some((e) => e.id === this._highlightedId)
    ) {
      this._highlightedId = null;
    }
    this._seleccionUnir = this._seleccionUnir.filter((id) =>
      this._state.getAll().some((e) => e.id === id),
    );

    this._aplicarResaltes();

    // El handle del modo Editar puede haber quedado sobre coordenadas que ya no
    // existen (si esto vino de un corte, una unión, o de una operación de
    // vértice que volvió del servidor): se rehace acá, que es el ÚNICO punto por
    // el que pasa todo repintado.
    // El lote en edición se vuelve a buscar por su `tempId`, no por el id que
    // tenía el motor: después de una operación de vértice el servidor contesta,
    // `_lotesDeLaBase` reinicia el estado y todos los lotes estrenan id local.
    //
    // Antes esto se resolvía con el id viejo, que ya no existía: `find` devolvía
    // `undefined`, `refrescar(null)` entendía "el lote se lo llevaron" y soltaba
    // la edición. Desde afuera se veía como que mover UN vértice cancelaba el
    // modo Editar y había que volver a clickear el mismo lote para seguir.
    // La guarda por herramienta es deliberada y va además del soltado de arriba:
    // nada de la edición se repinta si Editar no está encendida, pase lo que pase
    // con el estado interno.
    const enEdicion =
      this._herramienta === "editar" ? this._idEnEdicion() : null;
    if (enEdicion !== null) {
      // El id nuevo va junto con la geometría: el motor lo guarda para poder
      // nombrar el lote en la operación siguiente, y con el viejo el servidor no
      // lo encontraría.
      this._editEngine?.refrescar(
        this._state.getAll().find((e) => e.id === enEdicion)?.polygon ?? null,
        enEdicion,
      );

      // Y la capa WMS de vértices se vuelve a apuntar al lote con su caja NUEVA.
      //
      // No alcanza con repintarla (`refrescar`, más arriba): su extensión de datos
      // la fijó `mostrarVertices` con la caja del lote al SELECCIONARLO, y mover
      // un vértice cambia esa caja. Al arrastrar un vértice hacia afuera, su
      // posición nueva caía fuera de esos límites y Leaflet no pedía el tile: el
      // vértice movido no aparecía dibujado, o aparecía todavía en el lugar viejo
      // porque el tile que lo contenía ya no se repedía. El handle, que sí se
      // rehace acá, quedaba sobre un punto que el WMS no estaba mostrando.
      const cajaEnEdicion = this._state.bboxDe(enEdicion);
      if (this._tempIdEnEdicion && cajaEnEdicion) {
        this._wmsLotes?.mostrarVertices(this._tempIdEnEdicion, cajaEnEdicion);
      }
    }
  };

  /** Pasa a las capas WMS de resalte los lotes elegidos.
   *
   *  El resalte se pide por el `tempId` del lote (su uuid en el registro
   *  temporal), no por el id local: es lo que GeoServer conoce. Un lote recién
   *  creado por un corte no lo tiene hasta que su alta vuelve del servidor, así
   *  que hasta entonces no se puede resaltar — y tampoco hace falta, porque
   *  tampoco se está dibujando todavía.
   *
   *  **Gana `_highlightedId` sobre `_seleccionado`, y en ese orden.** Hay UNA
   *  capa de resalte para dos señales: la selección por click, que es
   *  persistente, y el foco del input de nombre en el sidebar, que es pasajero y
   *  lo está manejando el usuario en ese momento. Con la precedencia al revés
   *  —que es como estuvo— alcanzaba un click previo en el mapa para que el
   *  resalte quedara clavado en ese lote y recorrer los inputs del sidebar no
   *  cambiara nada.
   *
   *  Al soltar el input, su `blur` manda `setHighlighted(null)` y el resalte
   *  vuelve al lote seleccionado, si había uno.
   *
   *  Cuando los lotes eran capas de Leaflet esto no se presentaba: cada lote
   *  llevaba su propio estilo, así que las dos señales podían verse a la vez. Con
   *  una sola capa filtrada hay que elegir, y la que corresponde es la que el
   *  usuario está moviendo. */
  private _aplicarResaltes(): void {
    if (!this._wmsLotes) return;

    const seleccionado =
      this._highlightedId !== null ? this._highlightedId : this._seleccionado;
    const tempIdSel =
      seleccionado !== null ? this._state.tempIdDe(seleccionado) : null;
    this._wmsLotes.seleccionar(
      tempIdSel,
      seleccionado !== null ? this._state.bboxDe(seleccionado) : null,
    );

    const elegidos = this._seleccionUnir
      .map((id) => this._state.tempIdDe(id))
      .filter((id): id is string => !!id);
    this._wmsLotes.elegir(elegidos, this._cajaDe(this._seleccionUnir));
  }

  /** La caja que abarca varios lotes, para acotar los tiles del resalte. */
  private _cajaDe(ids: readonly number[]): [number, number, number, number] | null {
    let total: [number, number, number, number] | null = null;
    for (const id of ids) {
      const caja = this._state.bboxDe(id);
      if (!caja) continue;
      total = total
        ? [
            Math.min(total[0], caja[0]),
            Math.min(total[1], caja[1]),
            Math.max(total[2], caja[2]),
            Math.max(total[3], caja[3]),
          ]
        : caja;
    }
    return total;
  }

  /** Vuelve a pedirle los lotes a GeoServer.
   *
   *  Lo llama el paso 4 cuando el servidor CONFIRMÓ un cambio (ver
   *  `LotesTempSync`, parámetro `onPersistido`): un corte, una unión, un
   *  borrado o un nombre nuevo. Recién ahí la base tiene lo que esta capa
   *  dibuja; pedirlo antes traería la geometría vieja, o el nombre viejo en la
   *  etiqueta.
   *
   *  **Con `zona` se repinta sólo esa zona** (2026-10-27, pedido del usuario):
   *  la caja de los lotes que salieron, entraron o cambiaron de geometría o de
   *  nombre, la calcula quien llama (`RegistroFincaPaso4Render._zonaQueCambio`
   *  y `LotesTempSync`). Las etiquetas se repiden en una zona más ancha (ver
   *  `LotesWmsLayer.refrescar`). Sin `zona` se repinta TODO, que es lo que se
   *  hacía siempre: medido en pruebas, un solo corte pedía 22 imágenes.
   *
   *  Antes se repintaba todo a propósito: un corte reemplaza un lote por dos y
   *  un renombre mueve una etiqueta que GeoServer pudo haber desplazado para
   *  que entrara, así que el área afectada no siempre es la del lote tocado.
   *  **Para revertir** —si aparece un nombre cortado o un resto del dibujo
   *  anterior después de una operación—: que los llamadores no pasen la zona
   *  (`refrescarLotes()` a secas en `RegistroFincaPaso4Render`). */
  refrescarLotes(zona: [number, number, number, number] | null = null): void {
    const caja = this._state.bboxTotal();
    if (caja && this._map) {
      const limites = L.latLngBounds(
        L.latLng(caja[1], caja[0]),
        L.latLng(caja[3], caja[2]),
      );
      this._wmsLotes?.extender(limites);
    }
    this._wmsLotes?.refrescar(
      zona
        ? L.latLngBounds(L.latLng(zona[1], zona[0]), L.latLng(zona[3], zona[2]))
        : null,
    );
    // Los resaltes se repiden también: un lote recién creado acaba de estrenar
    // su `tempId`, y hasta este momento no se podía filtrar por él.
    this._aplicarResaltes();
    // Acá se soltaba el contorno naranja que el motor de dibujo pintaba sobre el
    // lote recién trazado, para que no quedara encima del que pinta el WMS. Ya no
    // hace falta: el motor no pinta ninguna forma cerrada cuando quien lo monta
    // le pide el TRAZO en vez de la pieza (ver `AoiDrawEngine.onTrazo`), que es
    // lo que hace el paso 4 desde que el lote lo arma el servidor.
  }

  /** Click en el mapa: elige el lote que haya debajo, según la herramienta.
   *
   *  **Con Dividir y con Dibujar no hace nada**: ahí los clicks son del trazo
   *  (`LoteSplitLineEngine` y `AoiDrawEngine`), que ponen un vértice por
   *  click. Cuando
   *  los lotes eran capas de Leaflet esto se resolvía al revés —el listener
   *  estaba en la capa y NO cortaba la propagación, para que el click llegara
   *  igual al mapa—; con un solo listener en el mapa hay que decidirlo acá. */
  private _alClickDelMapa = (ev: L.LeafletMouseEvent): void => {
    // Dividir tiene DOS pasos. Mientras espera, el click elige el lote a
    // dividir y es de este render; una vez elegido, los clicks son del trazo y
    // los toma el motor de la línea por su cuenta.
    if (this._herramienta === "dividir") {
      if (!this._toolbar.esperandoLoteParaDividir()) return;
      const elegido = this._state.enElPunto(ev.latlng.lng, ev.latlng.lat);
      if (elegido === null) return;
      void this._toolbar.comenzarCorteDeAsync(elegido);
      return;
    }

    // Dibujar: los clicks son del trazo (`AoiDrawEngine`), que pone un vértice
    // por click —y se apoya en los vértices de los lotes con el snap, así que
    // casi siempre cae sobre uno—. Sin esto, cada vértice puesto encima de un
    // lote lo dejaba además resaltado en amarillo, como si se lo hubiera
    // elegido. Mismo criterio que Dividir con su trazo: la herramienta que
    // dibuja se queda con el click.
    if (this._herramienta === "dibujar") return;

    const id = this._state.enElPunto(ev.latlng.lng, ev.latlng.lat);
    if (id === null) return;

    if (this._herramienta === "unir") {
      this._elegirParaUnir(id);
      return;
    }
    if (this._herramienta === "borrar") {
      this._elegirParaBorrar(id);
      return;
    }
    if (this._herramienta === "editar") {
      // Clickear el mismo lote lo suelta: es un toggle, igual que la selección
      // de una pieza en el paso 3.
      if (this._editEngine?.loteSeleccionado === id) {
        this._soltarEdicionDeVertices();
        return;
      }
      void this._editarVerticesDeAsync(id);
      return;
    }
    this._alternarSeleccion(id);
  };

  /** Pone un lote en edición de vértices: se leen de la base por gRPC, se
   *  prenden los que están en reposo (por WMS) y se le entrega la geometría al
   *  motor, que de ahí saca el handle que pone al vuelo bajo el cursor.
   *
   *  La geometría se lee de la BASE y no de la copia del modelo: es la que está
   *  dibujando el WMS y la que el servidor va a editar, y la inserción de un
   *  vértice nombra el tramo por su número en el anillo — un vértice repetido que
   *  la copia normalizada no tiene correría esa cuenta. Si el servidor no
   *  contesta se cae a la del modelo, que es el camino degradado. (Dividir dejó
   *  de leerla el 2026-10-27: ahí sólo importan las posiciones, ver
   *  `LoteSplitToolbar.comenzarCorteDeAsync`.) */
  private async _editarVerticesDeAsync(id: number): Promise<void> {
    const tempId = this._state.tempIdDe(id);
    // Un lote cuya alta todavía no volvió no se puede editar: el servidor no
    // tendría con qué nombrarlo.
    if (!tempId) return;

    const leida = await this._lotesWfs.leerAsync(this._registroId, tempId);
    if (this._herramienta !== "editar") return;

    const geometria =
      leida ?? this._state.getAll().find((e) => e.id === id)?.polygon;
    if (!geometria) return;

    this._editEngine?.seleccionar(id, geometria);
    // El `tempId` se recuerda porque el id LOCAL no sobrevive a la respuesta del
    // servidor (ver `_idEnEdicion`).
    this._tempIdEnEdicion = tempId;
    this._wmsLotes?.mostrarVertices(tempId, this._state.bboxDe(id));
  }

  /** Suelta el lote en edición de vértices. */
  private _soltarEdicionDeVertices(): void {
    this._editEngine?.soltar();
    this._tempIdEnEdicion = null;
    this._wmsLotes?.mostrarVertices(null);
  }

  /** El `tempId` (uuid del registro temporal) del lote que se está editando.
   *
   *  **Es lo único que identifica a ese lote entre una operación y la
   *  siguiente.** El id local no sirve: `LoteSplitState._nextId` sólo incrementa
   *  y `init()` no lo reinicia, así que cada respuesta del servidor —que llega
   *  por `_lotesDeLaBase`, o sea `init()`— le estrena id local a TODOS los lotes.
   *  El uuid, en cambio, es el mismo mientras el lote exista. */
  private _tempIdEnEdicion: string | null = null;

  /** El id local que tiene AHORA el lote en edición, o `null` si ya no está. */
  private _idEnEdicion(): number | null {
    if (!this._tempIdEnEdicion) return null;
    const entry = this._state
      .getAll()
      .find((e) => e.tempId === this._tempIdEnEdicion);
    return entry ? entry.id : null;
  }

  private readonly _lotesWfs = new LotesWfsService();

  /** Traduce la operación del motor a la que entiende el servidor.
   *
   *  El motor nombra el lote por su id LOCAL (el del modelo, un número); el
   *  servidor lo conoce por su uuid del registro temporal. La traducción vive
   *  acá porque es este render el que tiene el estado a mano. */
  private _alOperarVertice = (operacion: OperacionVertice): void => {
    const insercion = operacion.insercion;
    if (!insercion) {
      // Mover y quitar no nombran ningún lote: el vértice se identifica por su
      // posición, y el servidor lo busca en todos.
      this._onVertice?.({
        origen: operacion.origen,
        destino: operacion.destino,
      });
      return;
    }

    const tempId = this._state.tempIdDe(insercion.loteId);
    // Un lote cuya alta todavía no volvió no se puede editar: el servidor no
    // tendría con qué nombrarlo.
    if (!tempId) return;
    this._onVertice?.({
      ...operacion,
      insercion: { loteId: tempId, indiceArista: insercion.indiceArista },
    });
  };

  /** Notifica que hay una operación de vértice que mandar al servidor, con el
   *  lote ya nombrado como lo nombra el servidor. La atiende quien tiene el
   *  registro y el servicio gRPC. */
  setOnVertice(handler: (operacion: OperacionVerticeServidor) => void): void {
    this._onVertice = handler;
  }

  private _onVertice:
    | ((operacion: OperacionVerticeServidor) => void)
    | null = null;

  /** Cambio de herramienta: refresca el aviso y suelta todo resalte de lote.
   *
   *  **Ninguna herramienta hereda el lote seleccionado.** El resalte azul de
   *  `_seleccionado` es de la navegación SIN herramienta —click en un lote para
   *  ver cuál es y enfocar su input—, y cada herramienta tiene su propia forma
   *  de elegir sobre qué trabaja. Al entrar a una quedaba clavado en ese lote:
   *  los clicks siguientes los atiende la herramienta, que no toca
   *  `_seleccionado`, así que el azul seguía en el primero aunque el usuario
   *  eligiera otro para editar. Se suelta en CUALQUIER cambio de herramienta,
   *  también al apagarla: la selección pertenece al modo del que se sale. */
  private _onHerramientaChange = (
    herramienta: LoteSplitHerramienta | null,
  ): void => {
    this._herramienta = herramienta;
    this._seleccionado = null;
    // La selección la comparten Unir y Borrar (es el mismo resalte); salir de
    // los dos modos la vacía.
    if (herramienta !== "unir" && herramienta !== "borrar") {
      this._limpiarSeleccionUnir();
    }
    // Repintado explícito: `_limpiarSeleccionUnir` corta en seco cuando no
    // había nada elegido, y los modos Unir y Borrar ni siquiera pasan por él,
    // así que soltar `_seleccionado` podía no llegar nunca a la capa.
    this._aplicarResaltes();
    if (herramienta === "editar") {
      this._editEngine?.activar();
    } else {
      this._editEngine?.desactivar();
      // `_soltarEdicionDeVertices` y no sólo `mostrarVertices(null)`: lo que hay
      // que soltar es el LOTE en edición, no la capa. Antes se apagaba la capa y
      // `_tempIdEnEdicion` quedaba apuntando al último lote editado, así que el
      // repintado siguiente —escribir en el input de nombre del sidebar dispara
      // uno— volvía a prenderle los vértices con el botón Editar apagado.
      this._soltarEdicionDeVertices();
    }
    this._refreshAviso();
  };

  /* El rechazo de una operación de vértice ya no se arma acá: lo decide el
     servidor (`EditarVerticeLote`, que valida y, si no pasa, no toca nada) y lo
     muestra quien atiende la operación — `RegistroFincaPaso4Render`. Antes el
     motor validaba contra el modelo con turf y mandaba el motivo por su cuenta. */

  /** Primer click elige una pieza; el segundo dispara la validacion de
   *  adyacencia y la confirmacion. Volver a clickear la ya elegida la
   *  deselecciona, para poder corregir sin salir del modo. */
  /** Elige el lote, o lo suelta si ya estaba elegido. */
  private _alternarSeleccion(id: number): void {
    this._seleccionado = this._seleccionado === id ? null : id;
    this._renderLotes();
    if (this._herramienta === null) {
      this._onLoteSeleccionado?.(this._seleccionado);
    }
  }

  /** Un click en el modo Borrar: resalta el lote y pide confirmación. Un
   *  segundo click sobre el mismo lo deselecciona, igual que en Unir. */
  private _elegirParaBorrar(id: number): void {
    if (this._seleccionUnir.includes(id)) {
      this._limpiarSeleccionUnir();
      return;
    }

    this._seleccionUnir = [id];
    this._renderLotes();
    // **El modal espera al resalte.** El amarillo lo pinta GeoServer y tarda
    // una ida y vuelta; el modal es inmediato. Abriéndolos a la vez salía
    // primero el modal y el color después, debajo: había que decidir sin ver
    // qué lote se iba a borrar. Si mientras tanto se cambió de herramienta, no
    // se abre nada.
    // El `?? Promise.resolve()` no es decorativo: sin capa WMS el optional
    // chaining devuelve `undefined` y el `.then` no corre, o sea que el modal
    // no se abriría nunca.
    void (this._wmsLotes?.esperarResalteAsync() ?? Promise.resolve()).then(() => {
      if (this._herramienta !== "borrar") {
        this._limpiarSeleccionUnir();
        return;
      }
      this._toolbar.confirmarBorrado(id, () => {
        // Se haya borrado o cancelado, la selección se vacía: el lote puede ya
        // no existir, y dejarlo resaltado dejaría un id muerto en la lista.
        this._limpiarSeleccionUnir();
      });
    });
  }

  private _elegirParaUnir(id: number): void {
    const yaElegida = this._seleccionUnir.indexOf(id);
    if (yaElegida !== -1) {
      this._seleccionUnir.splice(yaElegida, 1);
      this._renderLotes();
      return;
    }

    this._seleccionUnir.push(id);
    this._renderLotes();
    if (this._seleccionUnir.length < 2) return;

    const [idA, idB] = this._seleccionUnir;
    // **El modal espera al resalte, igual que en Borrar.** Los dos lotes
    // elegidos los pinta GeoServer, así que entre el segundo click y el
    // amarillo hay una ida y vuelta más la pausa para alcanzar a verlos; el
    // modal es inmediato. Sin esto tapaba la selección antes de que el usuario
    // registrara QUÉ dos lotes está por unir — que acá importa todavía más que
    // en Borrar, porque son dos. Si mientras tanto se cambió de herramienta,
    // no se abre nada.
    void (this._wmsLotes?.esperarResalteAsync() ?? Promise.resolve()).then(() => {
      if (this._herramienta !== "unir") {
        this._limpiarSeleccionUnir();
        return;
      }
      this._toolbar.confirmarUnion(idA, idB, () => {
        // Se llame como se llame (unio, cancelo o no eran adyacentes), la
        // seleccion se vacia: si no, un segundo intento arrancaria con una
        // pieza ya elegida, o peor, con una que acaba de desaparecer al unirse.
        this._limpiarSeleccionUnir();
      });
    });
  }

  private _limpiarSeleccionUnir(): void {
    if (this._seleccionUnir.length === 0) return;
    this._seleccionUnir = [];
    this._renderLotes();
  }

  /** Quién responde si el trazo del modo Dibujar se puede autocerrar (ver
   *  `LoteSplitToolbar.setOnValidarAutocierre`). Lo pone el paso 4. */
  setOnValidarAutocierre(
    handler: (
      puntos: Position[],
    ) => Promise<{ valido: boolean; areaHas: number }>,
  ): void {
    this._toolbar.setOnValidarAutocierre(handler);
  }

  /** Fila de avisos: el nombre del paso (permanente) y, al lado, la ayuda de
   *  la herramienta activa (Dividir / Unir), que va y viene con ella. El
   *  markup lo arma `fx_filaAvisos`, compartido con el paso 3; esta clase solo
   *  decide qué textos van y si la fila queda visible. */
  private _refreshAviso(): void {
    if (!this._avisoEl) return;
    // Dividir cambia de texto cuando el lote ya está elegido: lo que sigue es
    // trazar, no elegir.
    const texto =
      this._herramienta === "dividir" &&
      !this._toolbar.esperandoLoteParaDividir()
        ? AVISO_DIVIDIR_TRAZANDO
        : this._herramienta === "editar" &&
            this._editEngine?.loteSeleccionado !== null &&
            this._editEngine?.loteSeleccionado !== undefined
          ? AVISO_EDITAR_VERTICES
          : this._herramienta
            ? AVISOS_PASO4[this._herramienta]
            : null;
    const html = fx_filaAvisos(
      { texto: this._avisoPaso, icono: ICONO_PASO },
      texto,
    );
    this._avisoEl.hidden = html === "";
    this._avisoEl.innerHTML = html;
  }

  private _fitToLotes(): void {
    if (!this._map) return;
    // La extensión sale del MODELO y no de un `FeatureGroup`: sin capas de
    // Leaflet no hay grupo al que preguntarle sus bounds.
    const caja = this._state.bboxTotal();
    if (!caja) return;
    const bounds = L.latLngBounds(
      L.latLng(caja[1], caja[0]),
      L.latLng(caja[3], caja[2]),
    );
    if (!bounds.isValid()) return;
    // Sin animación: así el `zoomstart` que dispara llega DENTRO de la
    // bandera, de forma síncrona. Con animación podría llegar después y
    // marcarse como si el usuario hubiera hecho zoom.
    this._encuadrando = true;
    try {
      this._map.fitBounds(bounds, { padding: [40, 40], animate: false });
    } finally {
      this._encuadrando = false;
    }

    // Las capas WMS nacen con una extensión degenerada para no pedir tiles al
    // vacío (ver `AoiWmsLayer`), así que hay que decirles dónde están los datos.
    // Va acá y no en `connect()` porque ahí todavía no hay lotes: los pone
    // `LoteSplitState` cuando termina de leer.
    //
    // Sólo los lotes: la capa de polígonos padre no se monta en este paso.
    this._wmsLotes?.extender(bounds);
  }

  private _ortomosaico: OrtomosaicoControl | null = null;
  private _wmsLotes: LotesWmsLayer | null = null;
  /** Los polígonos padre del registro, **en memoria y sin renderizar**. Ver
   *  `setPoligonosPadre`. */
  private _poligonosPadre: readonly Polygon[] = [];
}
